// match-audit.jsx — diagnostic view: every quote line, what it matched to,
import React from 'react';
import { firebase, fbAuth, fbDb, fbStorage } from './firebase.js';
import {
  fmtUSD, fmtDelta, safeUrl, firestoreSafe, toCSV, downloadFile, relativeTime,
  invoiceDateOf, invoiceInRange, dateRangePresets, useFocusTrap,
  ROLES, ROLE_LABEL, ROLE_COLOR,
  usesSubInvoices, useJobsStore, useJobData, invoiceStore, deleteJobDeep, useVendorRules,
  normalizeUOM, isFootageUOM, displayUOM, stickFeet, itemFootage, convertToQuoteBasis, summarizeMatch,
  isAutoSku, itemKeyOf, levenshtein, fracToNum, normalizeForMatch, matchNormalize, matchLineItems,
  aggregateInvoices, compareInvoices, materialState, classifyRows, MATERIAL_STATE_META,
  getJobTrades, getSegments, lineTotal, subtotal, tax, grandTotal, pricesDiffer,
  statedInvoiceTotal, statedInvoiceTax, invoiceSpend,
  SAMPLE_VENDORS, INVOICE_A, INVOICE_B, SEED_VENDOR_RULES,
} from './invodiff-data.jsx';
import { Icon } from './invodiff-input.jsx';
import { RowSourceLinks, DocProvenanceModal } from './doc-provenance.jsx';
import { suggestLinks } from './match-suggest.js';
// the match type, and the confidence, so pairings can be eyeballed for accuracy.
// Reads the same job data as the budget tracker / variance view and runs the
// shared matchLineItems so what you see here is exactly what those screens use.
//
// AI suggestions (match-suggest.js) layer on top: for the unmatched residue the
// deterministic matcher can't bridge, Claude proposes same-item links ranked by
// confidence. They surface inline on "not invoiced" rows for one-click PM
// confirmation; a confirm writes the same vendor `alias` rule a manual link does.

// How "trustworthy" a match type is — drives color + the "needs review" filter.
const MATCH_TIER = {
  manual:               { tier: 'high',   label: 'Manual' },
  alias:                { tier: 'high',   label: 'Linked' },
  exact_sku:            { tier: 'high',   label: 'SKU' },
  exact_desc:           { tier: 'high',   label: 'Exact desc' },
  cost_code:            { tier: 'review', label: 'Cost code' },
  partial:              { tier: 'review', label: 'Partial' },
  missing_from_invoice: { tier: 'none',   label: 'Not invoiced' },
  new_charge:           { tier: 'none',   label: 'New Material' },
};

// fuzzy_desc covers three sub-cases, distinguished by score:
//   score === 0   → normalized-exact (abbreviations/packaging differed only)
//   score integer → Levenshtein edit distance
//   score decimal → token-subset (stored as 1 − tokenScore)
// This screen's rows come from matchLineItems, whose shape predates
// aggregateInvoices' rows: a pair is { quote, iis } where `iis` are invoice
// items each tagged with the invoice they came from. The provenance helpers
// speak the aggregateInvoices shape, so adapt rather than fork them — one
// mapping from a row to its documents, four screens using it.
const pairAsRow = (pair) => ({
  quoteItem: pair.quote || null,
  breakdown: (pair.iis || []).map((ii) => ({ invoiceId: ii._invoiceId, srcItem: ii })),
});

function describeMatch(matchType, score) {
  if (matchType !== 'fuzzy_desc') {
    const m = MATCH_TIER[matchType] || { tier: 'review', label: matchType };
    return { ...m, detail: null };
  }
  if (score === 0)              return { tier: 'good',   label: 'Normalized', detail: 'abbrev/packaging' };
  if (Number.isInteger(score))  return { tier: 'review', label: 'Fuzzy',      detail: `edit dist ${score}` };
  return { tier: 'review', label: 'Token', detail: `${Math.round((1 - score) * 100)}% tokens` };
}

const TIER_TONE = { high: 'ok', good: 'info', review: 'warn', none: 'dim' };

// UOM display. Quantities carry the normalized *code* everywhere in the app
// (see qtyDisplay in invodiff-compare.jsx), so the code stays for density and
// consistency — but "C", "LN" and "BDL" mean nothing to a PM, so the human
// label rides along as a tooltip. Returns null when the label adds nothing.
const uomLabel = (uom) => {
  const label = displayUOM(uom);
  return label && label !== normalizeUOM(uom) ? label : null;
};

// Same thing in prose, for sentences rather than table cells: "LF (linear foot)".
const uomPhrase = (uom) => {
  const code = normalizeUOM(uom);
  if (!code) return '?';
  const label = uomLabel(uom);
  return label ? `${code} (${label})` : code;
};

// "12 EA" with the label on hover. Rendered next to a formatted quantity.
function UomTag({ uom }) {
  const code = normalizeUOM(uom);
  if (!code) return null;
  return <span title={uomLabel(uom) || undefined}>{' ' + code}</span>;
}

// AI-suggestion confidence → tone band (matches the variance/status palette).
const confTone = (c) => (c >= 0.85 ? 'ok' : c >= 0.7 ? 'accent' : 'warn');

function MatchAuditRow({ pair, quote, invoices, onPreview, linkCandidates, onLink, onUnlink, suggestion, onConfirmSuggestion, onDismissSuggestion }) {
  const { quote: qi, iis, matchType, matchScore } = pair;
  const row = React.useMemo(() => pairAsRow(pair), [pair]);
  const m = describeMatch(matchType, matchScore);
  const tone = TIER_TONE[m.tier] || 'dim';
  const [pickOther, setPickOther] = React.useState(false);

  const qUnit = qi && qi.unit != null ? fmtUSD(qi.unit) : '—';

  const canLink   = onLink && matchType === 'missing_from_invoice' && (linkCandidates || []).length > 0;
  const canUnlink = onUnlink && matchType === 'alias' && pair.ruleId;
  const hasSuggestion = !!(suggestion && matchType === 'missing_from_invoice');

  return (
    <div className={`mar mar-${tone}${hasSuggestion ? ' mar-suggested' : ''}`}>
      {/* Quote line */}
      <div className="mar-c mar-quote">
        {qi ? (
          <>
            <div className="mar-desc" title={qi.desc}>{qi.desc || <span className="mar-dash">(no description)</span>}</div>
            <div className="mar-sub">
              {qi.sku && !isAutoSku(qi.sku) && <span className="mar-sku mono">{qi.sku}</span>}
              <span className="mar-meta num">{(qi.qty ?? 0).toLocaleString()}<UomTag uom={qi.uom} /> @ {qUnit}</span>
            </div>
          </>
        ) : (
          <>
            <span className="mar-dash">— no quote line —</span>
            {matchType === 'new_charge' && pair.candidates && pair.candidates.length > 0 && (
              <div className="mar-cands">
                <span className="mar-cands-h">closest RFQ lines:</span>
                {pair.candidates.map((c) => (
                  <span key={(c.sku || '') + '|' + c.desc} className="mar-cand" title={c.desc}>
                    {c.desc}<span className="num mar-cand-c"> {c.confidence}%</span>
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Arrow */}
      <div className="mar-c mar-arrow">→</div>

      {/* Matched invoice line(s) */}
      <div className="mar-c mar-inv">
        {iis.length === 0
          ? (
            <>
              <span className="mar-dash">— not on any invoice —</span>

              {hasSuggestion && (
                <div className="mar-suggest">
                  <div className="mar-suggest-hd">
                    <Icon name="spark" size={12} className="mar-suggest-spark" />
                    <span className="mar-suggest-kicker">AI suggestion</span>
                    <span className={`mar-suggest-conf num mar-conf-${confTone(suggestion.confidence)}`}>
                      {Math.round(suggestion.confidence * 100)}%
                    </span>
                  </div>
                  <div className="mar-suggest-target">
                    {suggestion.invoiceRef && <span className="mar-inv-id mono">{suggestion.invoiceRef}</span>}
                    {suggestion.invoiceSku && <span className="mar-sku mono">{suggestion.invoiceSku}</span>}
                    <span className="mar-suggest-desc" title={suggestion.invoiceDesc}>{suggestion.invoiceDesc}</span>
                  </div>
                  {suggestion.reason && <div className="mar-suggest-reason">{suggestion.reason}</div>}
                  <div className="mar-suggest-acts">
                    <button className="mar-confirm" onClick={() => onConfirmSuggestion(qi, suggestion)}>
                      <Icon name="check" size={12} /> Confirm link
                    </button>
                    <button className="mar-suggest-alt" onClick={() => setPickOther((v) => !v)}>
                      {pickOther ? 'Hide list' : 'Pick another'}
                    </button>
                    <button className="mar-suggest-alt" onClick={() => onDismissSuggestion(qi)}>Dismiss</button>
                  </div>
                </div>
              )}

              {canLink && (!hasSuggestion || pickOther) && (
                <select
                  className="mar-link-select"
                  value=""
                  aria-label="Link this quote line to an invoice line"
                  onChange={(e) => {
                    const ii = (linkCandidates || []).find((c) => c._id === e.target.value);
                    if (ii) onLink(qi, ii);
                  }}
                >
                  <option value="" disabled>Same item under a different name? Link it…</option>
                  {linkCandidates.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c._invoiceId} · {c.sku ? c.sku + ' — ' : ''}{c.desc}
                    </option>
                  ))}
                </select>
              )}
            </>
          )
          : iis.map((ii, k) => (
              <div key={k} className="mar-inv-line">
                <span className="mar-inv-id mono">{ii._invoiceId}</span>
                <span className="mar-desc" title={ii.desc}>{ii.desc}</span>
                <span className="mar-meta num">{(ii.qty ?? 0).toLocaleString()}<UomTag uom={ii.uom} /> @ {ii.unit != null ? fmtUSD(ii.unit) : '—'}</span>
              </div>
            ))}
      </div>

      {/* Match type + confidence */}
      <div className="mar-c mar-type">
        <span className={`mar-badge mar-badge-${tone}`}>{m.label}</span>
        {pair.confidence != null && iis.length > 0 && (
          <span className="mar-conf-pct num">{pair.confidence}%</span>
        )}
        {pair.lowConfidence && <span className="mar-lowtag">low conf</span>}
        {pair.matchReason && iis.length > 0 && <span className="mar-why">{pair.matchReason}</span>}
        {(m.detail || pair.matchNote) && <span className="mar-detail">{m.detail || pair.matchNote}</span>}
        {iis.length > 1 && <span className="mar-detail">{iis.length} invoice lines</span>}
        {canUnlink && (
          <button className="mar-unlink" title="Remove this saved link"
            onClick={() => onUnlink(pair.ruleId)}>
            Unlink
          </button>
        )}
        {onPreview && (
          <RowSourceLinks row={row} quote={quote} invoices={invoices} onPreview={onPreview} />
        )}
      </div>
    </div>
  );
}

// Does this pair count as a "needs review" match? Pure (depends only on its
// argument and the module-scope describeMatch), so it lives at module scope
// rather than being rebuilt on every render.
const isReview = (p) =>
  p.matchType !== 'missing_from_invoice' &&
  p.matchType !== 'new_charge' &&
  describeMatch(p.matchType, p.matchScore).tier === 'review';

function MatchAuditView({ job, userId, vendorRules, setVendorRules, embedded = false }) {
  const [quote, setQuote]       = React.useState(null);
  const [invoices, setInvoices] = React.useState([]);
  const [preview, setPreview]   = React.useState(null); // { doc, sel } for the popup PDF viewer
  const openPreview = React.useCallback((doc, sel) => setPreview({ doc, sel }), []);
  const [synced, setSynced]     = React.useState(false);
  const [filter, setFilter]     = React.useState('all'); // all | review | matched | unmatched

  // AI suggestion state
  const [suggestions, setSuggestions] = React.useState([]);
  const [suggesting, setSuggesting]   = React.useState(false);
  const [suggestRan, setSuggestRan]   = React.useState(false);
  const [dismissed, setDismissed]     = React.useState(() => new Set());

  const isSample = job.id === '_sample';

  React.useEffect(() => {
    // New job context — drop any suggestions from the previous job.
    setSuggestions([]); setSuggestRan(false); setDismissed(new Set());
  }, [job.id]);

  // Live job doc + invoices via the shared dual-shape hook (invodiff-data.jsx).
  const live = useJobData(isSample ? null : job.id);
  React.useEffect(() => {
    if (isSample) {
      setQuote(INVOICE_A);
      setInvoices([INVOICE_B]);
      setSynced(true);
      return;
    }
    if (!live.synced) { setSynced(false); return; }
    if (live.exists) {
      setQuote(live.data.quote ?? null);
      setInvoices(live.invoices);
    }
    setSynced(true);
  }, [isSample, live]);

  // Alias rules for this vendor feed the matcher, so a link made here takes
  // effect immediately in this table AND in the budget/variance views.
  const vendor  = quote?.vendor || '';
  const aliases = React.useMemo(
    () => ((vendorRules || {})[vendor] || []).filter((r) => r.kind === 'alias'),
    [vendorRules, vendor]
  );

  const pairs = React.useMemo(
    () => quote && invoices.length > 0 ? matchLineItems(quote, invoices, { aliases }) : [],
    [quote, invoices, aliases]
  );

  // Invoice lines currently unmatched — the link targets offered on "missing" rows.
  const linkCandidates = React.useMemo(
    () => pairs.flatMap((p) => p.matchType === 'new_charge' ? [p.iis[0]] : []),
    [pairs]
  );

  // Persist a manual link as a vendor alias rule keyed by stable item identity
  // (real SKU, else normalized description) — it survives re-parses and applies
  // to every future job with this vendor.
  const handleLink = React.useCallback((qi, ii) => {
    if (!setVendorRules || !vendor) return;
    // Guardrail: refuse to link across units that can't be reconciled (e.g. a
    // 71 LF quote line to a 1 EA invoice line). convertToQuoteBasis already
    // handles legitimate conversions (stick↔LF, box packs, per-C/per-M); if it
    // still can't convert, the pair would net to no remaining qty and show a
    // bogus dollar variance — so we block it instead of creating that mess.
    const conv = (typeof convertToQuoteBasis === 'function') ? convertToQuoteBasis(qi, ii) : { compatible: true };
    if (!conv.compatible) {
      const qU = uomPhrase(qi.uomNorm || qi.uom);
      const iU = uomPhrase(ii.uomNorm || ii.uom);
      window.alert(
        `Can't link these — unit mismatch.\n\n` +
        `Quote line is priced in ${qU}, invoice line in ${iU}. ` +
        `They can't be reconciled into a remaining quantity or a meaningful variance.\n\n` +
        `If they really are the same material, fix the unit on one of the source lines first.`);
      return;
    }
    const label = (it) => (it.sku && !isAutoSku(it.sku)) ? it.sku : (it.desc || '').slice(0, 40);
    const rule = {
      id:         'r' + Date.now(),
      kind:       'alias',
      added:      new Date().toISOString().slice(0, 10),
      quoteKey:   itemKeyOf(qi),
      invoiceKey: itemKeyOf(ii),
      pattern:    `${label(qi)} ↔ ${label(ii)}`,
      note:       `Linked quote line "${qi.desc}" to invoice line "${ii.desc}" (${ii._invoiceId}).`,
    };
    setVendorRules((prev) => ({ ...prev, [vendor]: [...((prev || {})[vendor] || []), rule] }));
  }, [setVendorRules, vendor]);

  const handleUnlink = React.useCallback((ruleId) => {
    if (!setVendorRules || !vendor) return;
    setVendorRules((prev) => {
      const next = { ...prev };
      next[vendor] = (next[vendor] || []).filter((r) => r.id !== ruleId);
      if (next[vendor].length === 0) delete next[vendor];
      return next;
    });
  }, [setVendorRules, vendor]);

  // Ask Claude to reconcile the residue (match-suggest.js → /api/claude on Opus).
  const handleSuggest = React.useCallback(async () => {
    if (!quote || invoices.length === 0 || typeof window.suggestLinks !== 'function') return;
    setSuggesting(true);
    try {
      const res = await window.suggestLinks(quote, invoices, { aliases });
      setSuggestions(res);
    } catch (e) {
      console.error('match suggestions failed:', e);
      setSuggestions([]);
    } finally {
      setSuggesting(false);
      setSuggestRan(true);
    }
  }, [quote, invoices, aliases]);

  // Resolve a suggestion's invoice line and persist it through the manual-link path.
  const handleConfirmSuggestion = React.useCallback((qi, suggestion) => {
    const ii = linkCandidates.find((c) => c._id === suggestion.invoiceId);
    if (ii) handleLink(qi, ii);
  }, [linkCandidates, handleLink]);

  const handleDismiss = React.useCallback((qi) => {
    setDismissed((prev) => new Set(prev).add(qi._id));
  }, []);

  // Best (highest-confidence) live suggestion per quote line: skip dismissed ones
  // and any whose invoice line has since been claimed by a confirmed link.
  const candidateIds = React.useMemo(() => new Set(linkCandidates.map((c) => c._id)), [linkCandidates]);
  const suggestionByQuoteId = React.useMemo(() => {
    const map = {};
    for (const s of suggestions) { // pre-sorted by confidence desc
      if (dismissed.has(s.quoteId) || !candidateIds.has(s.invoiceId)) continue;
      if (!map[s.quoteId]) map[s.quoteId] = s;
    }
    return map;
  }, [suggestions, dismissed, candidateIds]);

  // Accepted/ignored items (resolved from the Issues desk) shouldn't inflate the
  // audit's "new charges" / "not invoiced" tallies — that's what made this panel
  // disagree with the Exceptions list. Honor the same ignore rules here.
  const ignoreRules = React.useMemo(
    () => ((vendorRules || {})[vendor] || []).filter((r) => r.kind === 'ignore'),
    [vendorRules, vendor]
  );
  const isAccepted = React.useCallback((p) => {
    if (ignoreRules.length === 0) return false;
    const item = p.quote || p.iis[0];
    if (!item) return false;
    const key = itemKeyOf(item);
    const sku = p.quote?.sku || p.iis[0]?.sku || '—';
    return ignoreRules.some((r) =>
      (r.quoteKey && r.quoteKey === key) ||
      r.pattern === '*' || r.pattern === sku || (sku !== '—' && sku.startsWith(r.pattern)));
  }, [ignoreRules]);

  const stats = React.useMemo(() => {
    const s = { matched: 0, review: 0, missing: 0, newCharge: 0, accepted: 0 };
    for (const p of pairs) {
      if (isAccepted(p)) { s.accepted++; continue; }
      if (p.matchType === 'missing_from_invoice') s.missing++;
      else if (p.matchType === 'new_charge') s.newCharge++;
      else {
        s.matched++;
        if (describeMatch(p.matchType, p.matchScore).tier === 'review') s.review++;
      }
    }
    return s;
  }, [pairs, isAccepted]);

  const shown = pairs.filter((p) => {
    if (filter === 'all') return true;
    if (filter === 'review') return !isAccepted(p) && isReview(p);
    // Accepted off-quote items read as settled, so they group with "matched".
    if (filter === 'matched') return isAccepted(p) || (p.matchType !== 'missing_from_invoice' && p.matchType !== 'new_charge');
    if (filter === 'unmatched') return !isAccepted(p) && (p.matchType === 'missing_from_invoice' || p.matchType === 'new_charge');
    return true;
  });

  if (!synced) {
    return <div className="ma-wrap"><div className="ma-loading"><div className="ma-spin" /></div><MatchAuditStyles /></div>;
  }
  if (!quote || invoices.length === 0) {
    return (
      <div className="ma-wrap">
        <div className="ma-state">
          <div className="ma-state-icon"><Icon name="search" size={28} /></div>
          <div className="ma-state-h">Nothing to audit yet</div>
          <div className="ma-state-sub">Add a quote and at least one invoice in the Invoices tab — line matching will appear here.</div>
        </div>
        <MatchAuditStyles />
      </div>
    );
  }

  const FILTERS = [
    ['all', 'All', pairs.length],
    ['review', 'Needs review', stats.review],
    ['matched', 'Matched', stats.matched + stats.accepted],
    ['unmatched', 'Unmatched', stats.missing + stats.newCharge],
  ];

  const canSuggest   = !!setVendorRules && stats.missing > 0 && linkCandidates.length > 0
                       && typeof window.suggestLinks === 'function';
  const shownCount   = Object.keys(suggestionByQuoteId).length;

  return (
    <div className={`ma-wrap${embedded ? ' ma-embedded' : ''}`}>
      {!embedded && (
        <div className="ma-hero">
          <div className="ma-hero-tag mono">JOB {job.jobNumber} · MATCH AUDIT</div>
          <h1 className="ma-hero-h">Match Audit</h1>
          <p className="ma-hero-sub">
            Every quote line, what it paired to, and how confident the match is. Amber rows used a fuzzy,
            token, or partial match — verify them against the source documents.
          </p>
        </div>
      )}

      <div className="ma-toolbar">
        <div className="ma-stats">
          <div className="ma-stat"><span className="ma-v num">{stats.matched}</span><span className="ma-l">matched</span></div>
          <div className="ma-stat ma-stat-warn"><span className="ma-v num">{stats.review}</span><span className="ma-l">need review</span></div>
          <div className="ma-stat"><span className="ma-v num">{stats.missing}</span><span className="ma-l">not invoiced</span></div>
          <div className="ma-stat"><span className="ma-v num">{stats.newCharge}</span><span className="ma-l">new charges</span></div>
        </div>
        {canSuggest && (
          <button className="btn btn-primary btn-sm ma-suggest-btn" onClick={handleSuggest} disabled={suggesting}>
            <Icon name="spark" size={14} />
            {suggesting ? 'Analyzing…' : suggestRan ? 'Re-run suggestions' : 'Suggest matches'}
          </button>
        )}
      </div>

      {suggestRan && !suggesting && (
        <div className={`ma-suggest-banner ${shownCount > 0 ? 'is-found' : 'is-empty'}`}>
          <Icon name="spark" size={13} />
          {shownCount > 0
            ? <span>{shownCount} likely match{shownCount !== 1 ? 'es' : ''} found — review and confirm on the highlighted rows below.</span>
            : <span>No confident same-item matches found among the unmatched lines.</span>}
        </div>
      )}

      <div className="ma-note">
        Quote lines are matched strongest→weakest: saved links → SKU → exact description → normalized → fuzzy →
        token → cost code → partial. On a &ldquo;not invoiced&rdquo; row, use <strong>Suggest matches</strong> to
        have Claude find same-item lines a vendor renamed, or <em>Link it…</em> to pair one yourself — either way the
        link is saved for this vendor and applies to every future job.
      </div>

      <div className="ma-filters">
        {FILTERS.map(([key, label, count]) => (
          <button key={key} className={`ma-filter ${filter === key ? 'is-on' : ''}`} onClick={() => setFilter(key)}>
            {label}<span className="ma-filter-count num">{count}</span>
          </button>
        ))}
      </div>

      <div className="ma-table">
        <div className="ma-thead">
          <div className="mar-c">Quote line (RFQ)</div>
          <div className="mar-c mar-arrow"></div>
          <div className="mar-c">Matched invoice line(s)</div>
          <div className="mar-c">Match</div>
        </div>
        {shown.map((p) => (
          <MatchAuditRow key={p.quote?._id ?? p.iis[0]._id} pair={p}
            quote={quote} invoices={invoices} onPreview={openPreview}
            linkCandidates={linkCandidates}
            onLink={setVendorRules ? handleLink : undefined}
            onUnlink={setVendorRules ? handleUnlink : undefined}
            suggestion={p.quote ? suggestionByQuoteId[p.quote._id] : undefined}
            onConfirmSuggestion={handleConfirmSuggestion}
            onDismissSuggestion={handleDismiss} />
        ))}
        {shown.length === 0 && <div className="ma-empty">No rows for this filter.</div>}
      </div>

      {preview && (
        <DocProvenanceModal invoice={preview.doc} initialSel={preview.sel}
          onClose={() => setPreview(null)} />
      )}

      <MatchAuditStyles />
    </div>
  );
}

const MatchAuditStyles = () => (
  <style>{`
    .ma-wrap { max-width: 1400px; margin: 0 auto; padding: 40px 24px 64px; display: flex; flex-direction: column; gap: 18px; }
    /* Embedded inside the Issues "Match audit" disclosure — drop the page chrome. */
    .ma-embedded { padding: 4px 0 0; max-width: none; }
    @keyframes dz-spin { to { transform: rotate(360deg); } }

    /* ── Loading / empty ─────────────────────────────────────────── */
    .ma-loading { display: flex; align-items: center; justify-content: center; min-height: 50vh; }
    .ma-spin { width: 28px; height: 28px; border-radius: 50%; border: 2.5px solid var(--border); border-top-color: var(--accent); animation: dz-spin .7s linear infinite; }
    .ma-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; min-height: 40vh; text-align: center; }
    .ma-state-icon { color: var(--fg-dim); opacity: 0.4; }
    .ma-state-h { font-size: 15px; font-weight: 600; color: var(--fg-muted); }
    .ma-state-sub { font-size: 13px; color: var(--fg-dim); max-width: 360px; line-height: 1.55; }
    .ma-empty { padding: 32px; text-align: center; color: var(--fg-dim); font-size: 13px; }

    /* ── Hero (unified eyebrow → title → sub) ─────────────────────── */
    .ma-hero-tag { font-size: 11px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); margin-bottom: 6px; }
    .ma-hero-h   { font-size: 30px; font-weight: 600; letter-spacing: -0.025em; margin: 0 0 8px; }
    .ma-hero-sub { font-size: 14.5px; color: var(--fg-muted); margin: 0; line-height: 1.55; max-width: 620px; }

    /* ── Toolbar: KPI stats + suggest CTA ─────────────────────────── */
    .ma-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
    .ma-stats { display: flex; gap: 10px; flex-wrap: wrap; }
    .ma-stat { background: var(--surface); border: 1px solid var(--border); border-radius: var(--r); padding: 8px 14px; min-width: 96px; box-shadow: var(--shadow-1); }
    .ma-stat-warn { border-color: oklch(from var(--warn) l calc(c*.5) h / 0.45); background: oklch(from var(--warn) l c h / 0.04); }
    .ma-v { display: block; font-size: 20px; font-weight: 600; letter-spacing: -0.02em; }
    .ma-stat-warn .ma-v { color: var(--warn); }
    .ma-l { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--fg-dim); }
    .ma-suggest-btn { display: inline-flex; align-items: center; gap: 7px; flex-shrink: 0; }

    .ma-suggest-banner {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 14px; border-radius: var(--r); font-size: 13px; line-height: 1.4;
      border: 1px solid var(--border);
    }
    .ma-suggest-banner.is-found { background: oklch(from var(--accent) l c h / 0.07); border-color: oklch(from var(--accent) l calc(c*.5) h / 0.4); color: var(--fg); }
    .ma-suggest-banner.is-found svg { color: var(--accent); }
    .ma-suggest-banner.is-empty { background: var(--surface-2); color: var(--fg-muted); }
    .ma-suggest-banner.is-empty svg { color: var(--fg-dim); }

    .ma-note { font-size: 12px; color: var(--fg-muted); line-height: 1.55; max-width: 880px; }
    .ma-note strong { color: var(--accent); font-weight: 600; }

    .ma-filters { display: flex; gap: 6px; flex-wrap: wrap; }
    .ma-filter { appearance: none; border: 1px solid var(--border); background: var(--surface); color: var(--fg-muted);
      padding: 5px 12px; border-radius: 999px; font: inherit; font-size: 12px; cursor: pointer;
      display: inline-flex; align-items: center; gap: 6px; transition: color .12s, background .12s, border-color .12s; }
    .ma-filter:hover:not(.is-on) { color: var(--fg); }
    .ma-filter.is-on { background: oklch(from var(--accent) l c h / 0.14); color: var(--accent); border-color: oklch(from var(--accent) l calc(c*.5) h / .4); font-weight: 600; }
    .ma-filter-count { font-size: 10.5px; background: var(--surface-3); color: var(--fg-dim); padding: 1px 6px; border-radius: 999px; }
    .ma-filter.is-on .ma-filter-count { background: oklch(from var(--accent) l c h / 0.22); color: var(--accent); }

    /* ── Table ───────────────────────────────────────────────────── */
    .ma-table { border: 1px solid var(--border); border-radius: var(--r-lg); overflow: hidden; background: var(--surface); box-shadow: var(--shadow-1); }
    .ma-thead, .mar { display: grid; grid-template-columns: minmax(220px,1fr) 26px minmax(260px,1.3fr) 150px; align-items: start; }
    .ma-thead { background: var(--surface-2); border-bottom: 1px solid var(--border); font-size: 10px;
      text-transform: uppercase; letter-spacing: 0.07em; color: var(--fg-dim); font-weight: 600; }
    .ma-thead .mar-c { padding: 8px 12px; }
    .mar { border-bottom: 1px solid var(--border); font-size: 12.5px; position: relative; }
    .mar:last-child { border-bottom: 0; }
    .mar-ok    { background: oklch(from var(--ok)   l c h / 0.05); }
    .mar-warn  { background: oklch(from var(--warn) l c h / 0.07); }
    .mar-dim   { background: transparent; }
    /* A row with a live AI suggestion gets an accent rail so the eye lands on it. */
    .mar-suggested::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 2px; background: var(--accent); }
    .mar-c { padding: 9px 12px; min-width: 0; }
    .mar-arrow { color: var(--fg-dim); text-align: center; padding-left: 0; padding-right: 0; }
    .mar-desc { font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .mar-sub { display: flex; gap: 8px; align-items: center; margin-top: 2px; }
    .mar-sku { font-size: 10.5px; background: var(--surface-3); padding: 1px 5px; border-radius: 3px; color: var(--fg-muted); }
    .mar-meta { font-size: 11px; color: var(--fg-dim); }
    .mar-inv-line { display: flex; flex-direction: column; gap: 1px; padding: 2px 0; }
    .mar-inv-line + .mar-inv-line { border-top: 1px dashed var(--border); margin-top: 3px; padding-top: 4px; }
    .mar-inv-id { font-size: 10px; color: var(--fg-dim); }
    .mar-badge { display: inline-block; font-size: 10.5px; font-weight: 600; padding: 2px 7px; border-radius: 4px; }
    .mar-badge-ok   { background: oklch(from var(--ok)   l c h / 0.18); color: var(--ok); }
    .mar-badge-info { background: oklch(from var(--accent) l c h / 0.16); color: var(--accent); }
    .mar-badge-warn { background: oklch(from var(--warn) l c h / 0.20); color: var(--warn); }
    .mar-badge-dim  { background: var(--surface-3); color: var(--fg-dim); }
    .mar-detail { display: block; font-size: 10px; color: var(--fg-dim); margin-top: 3px; }
    .mar-conf-pct { font-size: 10.5px; font-weight: 600; color: var(--fg-muted); margin-left: 6px; }
    .mar-why { display: inline-block; font-size: 9.5px; color: var(--fg-dim); font-family: "IBM Plex Mono", monospace; margin-left: 6px; text-transform: uppercase; letter-spacing: .02em; }
    .mar-lowtag { display: inline-block; font-size: 9px; font-weight: 600; color: var(--warn); background: var(--warn-bg); border-radius: 3px; padding: 1px 5px; margin-left: 6px; }
    .mar-cands { margin-top: 5px; display: flex; flex-direction: column; gap: 2px; }
    .mar-cands-h { font-size: 9px; text-transform: uppercase; letter-spacing: .03em; color: var(--fg-dim); }
    .mar-cand { font-size: 10.5px; color: var(--fg-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .mar-cand-c { color: var(--fg-dim); }
    .mar-dash { color: var(--fg-dim); font-style: italic; font-size: 11.5px; }

    /* ── AI suggestion card ──────────────────────────────────────── */
    .mar-suggest {
      margin-top: 8px; border-radius: var(--r);
      border: 1px solid oklch(from var(--accent) l calc(c*.5) h / 0.4);
      background: oklch(from var(--accent) l c h / 0.06);
      padding: 8px 10px; display: flex; flex-direction: column; gap: 6px;
    }
    .mar-suggest-hd { display: flex; align-items: center; gap: 6px; }
    .mar-suggest-spark { color: var(--accent); }
    .mar-suggest-kicker { font-size: 9.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--accent); }
    .mar-suggest-conf { margin-left: auto; font-size: 12px; font-weight: 700; }
    .mar-conf-ok     { color: var(--ok); }
    .mar-conf-accent { color: var(--accent); }
    .mar-conf-warn   { color: var(--warn); }
    .mar-suggest-target { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
    .mar-suggest-desc { font-size: 12px; font-weight: 500; color: var(--fg); overflow: hidden; text-overflow: ellipsis; }
    .mar-suggest-reason { font-size: 11px; color: var(--fg-muted); line-height: 1.45; font-style: italic; }
    .mar-suggest-acts { display: flex; align-items: center; gap: 6px; margin-top: 1px; }
    .mar-confirm {
      appearance: none; cursor: pointer; display: inline-flex; align-items: center; gap: 5px;
      font: inherit; font-size: 11.5px; font-weight: 600;
      background: var(--accent); color: var(--accent-fg); border: 0;
      border-radius: var(--r-sm); padding: 4px 10px;
      transition: filter .12s;
    }
    .mar-confirm:hover { filter: brightness(1.06); }
    .mar-suggest-alt {
      appearance: none; cursor: pointer; font: inherit; font-size: 11px; color: var(--fg-muted);
      background: transparent; border: 1px solid var(--border); border-radius: var(--r-sm); padding: 3px 9px;
      transition: color .12s, border-color .12s;
    }
    .mar-suggest-alt:hover { color: var(--fg); border-color: var(--border-strong); }

    .mar-link-select {
      display: block; margin-top: 6px; max-width: 100%;
      font: inherit; font-size: 11px; color: var(--fg-muted);
      background: var(--surface-2); border: 1px dashed var(--border);
      border-radius: var(--r); padding: 4px 6px; cursor: pointer;
    }
    .mar-link-select:hover { border-color: var(--accent); color: var(--fg); }
    .mar-unlink {
      display: block; margin-top: 5px; appearance: none; cursor: pointer;
      font: inherit; font-size: 10.5px; color: var(--fg-dim);
      background: transparent; border: 1px solid var(--border);
      border-radius: 4px; padding: 1px 7px;
    }
    .mar-unlink:hover { color: var(--bad); border-color: var(--bad); }

    @media (max-width: 780px) {
      .ma-thead, .mar { grid-template-columns: 1fr 20px minmax(0,1.1fr) 110px; }
      .ma-wrap { padding: 28px 16px 48px; }
    }
  `}</style>
);

export { MatchAuditView, describeMatch, MATCH_TIER };
